import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Flex, Heading, Text, Button, SimpleGrid, Card, CardHeader,
  CardBody, Icon, Badge, Spinner, Alert, AlertIcon, Divider, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl,
  FormLabel, Input, useDisclosure, useToast,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { MdSchool, MdEventAvailable, MdPlace, MdPerson, MdAccessTime } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/AxiosInstance';

const fmtDate = (d: any) =>
  d ? new Date(d).toLocaleDateString('es-MX', { dateStyle: 'long' }) : 'Por definir';

const TalleresIMMPublico: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [talleres, setTalleres] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tallerSel, setTallerSel] = useState<any>(null);
  const [insc, setInsc] = useState({ nombre: '', telefono: '', email: '' });
  const [enviando, setEnviando] = useState(false);
  const [inscError, setInscError] = useState('');
  const [okFolio, setOkFolio] = useState<any>(null);

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/imm/talleres/portal');
      setTalleres(Array.isArray(data) ? data : (data?.data || []));
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudieron cargar los talleres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const abrirInscripcion = (t: any) => {
    setTallerSel(t);
    setInsc({ nombre: '', telefono: '', email: '' });
    setInscError('');
    setOkFolio(null);
    onOpen();
  };

  const enviarInscripcion = async () => {
    if (!insc.nombre.trim()) {
      setInscError('El nombre es obligatorio.');
      return;
    }
    if (insc.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(insc.email)) {
      setInscError('El correo electrónico no es válido.');
      return;
    }
    setInscError('');
    setEnviando(true);
    try {
      const { data } = await axiosInstance.post(`/imm/talleres/${tallerSel.id}/inscripcion-portal`, insc);
      setOkFolio(data);
      toast({ title: 'Inscripción registrada', status: 'success', duration: 3000 });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e.message || 'No se pudo registrar la inscripción';
      setInscError(msg);
      toast({ title: msg, status: 'error', duration: 4000 });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Box bg="#5b182b" py={8} px={{ base: 4, md: 10 }} color="white" textAlign="center">
        <Flex justify="center" align="center" mb={2}>
          <Box bg="white" borderRadius="full" w={14} h={14} display="flex" alignItems="center" justifyContent="center" mr={4}>
            <Icon as={MdSchool} color="#5b182b" boxSize={8} />
          </Box>
          <Box textAlign="left">
            <Heading size="lg">Talleres y Actividades</Heading>
            <Text fontSize="sm" opacity={0.85}>Instancia Municipal de la Mujer</Text>
          </Box>
        </Flex>
        <Text fontSize="sm" opacity={0.75} mt={2}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
      </Box>

      <Box py={8} px={{ base: 4, md: 10 }} maxW="1000px" mx="auto">
        <VStack spacing={6} align="stretch">
          <HStack>
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500"
              onClick={() => navigate('/portal/2026')}>
              Portal Ciudadano
            </Button>
          </HStack>

          {loading && (
            <Flex justify="center" py={16}><Spinner size="xl" color="#5b182b" thickness="4px" /></Flex>
          )}

          {!loading && error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text>{error}</Text>
                <Button mt={2} size="xs" onClick={cargar}>Reintentar</Button>
              </Box>
            </Alert>
          )}

          {!loading && !error && talleres.length === 0 && (
            <Card shadow="sm">
              <CardBody textAlign="center" py={12}>
                <Icon as={MdEventAvailable} boxSize={12} color="gray.300" mb={3} />
                <Heading size="sm" color="gray.500">No hay talleres publicados</Heading>
                <Text fontSize="sm" color="gray.400" mt={1}>
                  Por el momento no existen talleres disponibles. Vuelva a consultar más tarde.
                </Text>
              </CardBody>
            </Card>
          )}

          {!loading && !error && talleres.length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {talleres.map((t) => {
                const inscritos = t?._count?.inscripciones ?? 0;
                const cupoLleno = t.cupo != null && inscritos >= t.cupo;
                return (
                  <Card key={t.id} shadow="md" _hover={{ shadow: 'lg' }} transition="all 0.2s"
                    display="flex" flexDir="column">
                    <CardHeader bg="gray.50" borderTopRadius="md" pb={3}>
                      <HStack justify="space-between" align="start">
                        <Heading size="sm" color="#5b182b">{t.nombre}</Heading>
                        {t.tipo && <Badge colorScheme="purple">{t.tipo}</Badge>}
                      </HStack>
                    </CardHeader>
                    <CardBody display="flex" flexDir="column" flex="1">
                      <VStack align="stretch" spacing={2} flex="1">
                        {t.descripcion && (
                          <Text fontSize="sm" color="gray.600" noOfLines={3}>{t.descripcion}</Text>
                        )}
                        <HStack fontSize="sm" color="gray.600">
                          <Icon as={MdEventAvailable} color="#5b182b" />
                          <Text>{fmtDate(t.fecha)}{t.horario ? ` · ${t.horario}` : ''}</Text>
                        </HStack>
                        {t.sede && (
                          <HStack fontSize="sm" color="gray.600">
                            <Icon as={MdPlace} color="#5b182b" /><Text>{t.sede}</Text>
                          </HStack>
                        )}
                        {t.instructor && (
                          <HStack fontSize="sm" color="gray.600">
                            <Icon as={MdPerson} color="#5b182b" /><Text>{t.instructor}</Text>
                          </HStack>
                        )}
                        {t.cupo != null && (
                          <HStack fontSize="sm" color="gray.600">
                            <Icon as={MdAccessTime} color="#5b182b" />
                            <Text>Cupo: {inscritos}/{t.cupo}</Text>
                            {cupoLleno && <Badge colorScheme="red">Cupo lleno</Badge>}
                          </HStack>
                        )}
                      </VStack>
                      <Divider my={3} />
                      <Button colorScheme="purple" size="sm" w="full" isDisabled={cupoLleno}
                        onClick={() => abrirInscripcion(t)}>
                        {cupoLleno ? 'Cupo lleno' : 'Inscribirme →'}
                      </Button>
                    </CardBody>
                  </Card>
                );
              })}
            </SimpleGrid>
          )}

          <Text fontSize="xs" color="gray.400" textAlign="center">
            Instancia Municipal de la Mujer · H. Ayuntamiento de Totolac · Tlaxcala
          </Text>
        </VStack>
      </Box>

      {/* Modal de inscripción rápida */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="#5b182b">Inscripción al taller</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {okFolio ? (
              <VStack spacing={3} py={4} textAlign="center">
                <Icon as={CheckCircleIcon} boxSize={12} color="green.400" />
                <Heading size="sm" color="green.600">¡Inscripción registrada!</Heading>
                <Text fontSize="sm" color="gray.600">
                  {okFolio.mensaje || 'Su inscripción fue registrada correctamente.'}
                </Text>
                {okFolio.estatus && <Badge colorScheme="purple">{okFolio.estatus}</Badge>}
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                {tallerSel && (
                  <Text fontSize="sm" color="gray.500">
                    Taller: <b>{tallerSel.nombre}</b>
                  </Text>
                )}
                {inscError && <Alert status="error" borderRadius="md"><AlertIcon />{inscError}</Alert>}
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nombre completo</FormLabel>
                  <Input value={insc.nombre} onChange={(e) => setInsc({ ...insc, nombre: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Teléfono</FormLabel>
                  <Input value={insc.telefono} onChange={(e) => setInsc({ ...insc, telefono: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Correo electrónico</FormLabel>
                  <Input type="email" value={insc.email} onChange={(e) => setInsc({ ...insc, email: e.target.value })} />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {okFolio ? (
              <Button colorScheme="purple" onClick={() => { onClose(); cargar(); }}>Cerrar</Button>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
                <Button colorScheme="purple" onClick={enviarInscripcion} isLoading={enviando}
                  loadingText="Enviando…">
                  Confirmar inscripción
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TalleresIMMPublico;
